/**
 * S3FileWidget component - Upload files to Hetzner S3 storage
 * @module components/Widgets/S3FileWidget
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Image, Dimmer, Loader, Message } from 'semantic-ui-react';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';
import deleteSVG from '@plone/volto/icons/delete.svg';
import uploadSVG from '@plone/volto/icons/upload.svg';
import { Icon, FormFieldWrapper } from '@plone/volto/components';
import { validateFileUploadSize, getBaseUrl } from '@plone/volto/helpers';
import { uploadS3File } from '../../actions/s3Upload';

const Dropzone = loadable(() => import('react-dropzone'));

const imageMimetypes = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/jpg',
  'image/gif',
  'image/svg+xml',
];

const messages = defineMessages({
  releaseDrag: {
    id: 'Drop files here ...',
    defaultMessage: 'Drop files here ...',
  },
  editFile: {
    id: 'Drop file here to replace the existing file',
    defaultMessage: 'Drop file here to replace the existing file',
  },
  fileDrag: {
    id: 'Drop file here to upload to S3',
    defaultMessage: 'Drop file here to upload to S3',
  },
  replaceFile: {
    id: 'Replace existing file',
    defaultMessage: 'Replace existing file',
  },
  addNewFile: {
    id: 'Choose a file to upload to S3',
    defaultMessage: 'Choose a file to upload to S3',
  },
  uploading: {
    id: 'Uploading to S3...',
    defaultMessage: 'Uploading to S3...',
  },
  uploadSuccess: {
    id: 'File uploaded successfully to S3',
    defaultMessage: 'File uploaded successfully to S3',
  },
  uploadError: {
    id: 'Error uploading file to S3',
    defaultMessage: 'Error uploading file to S3',
  },
});

/**
 * S3FileWidget component - Uploads files to S3 instead of creating File objects
 * @function S3FileWidget
 * @returns {string} Markup of the component.
 *
 * To use it, in schema properties, declare a field like:
 *
 * ```jsx
 * {
 *  title: "S3 File",
 *  widget: 's3file',
 * }
 * ```
 *
 * The widget stores the S3 URL in the field value.
 * You can also configure the S3 folder:
 *
 * ```jsx
 * {
 *  title: "S3 File",
 *  widget: 's3file',
 *  widgetOptions: {
 *    s3folder: 'documents',  // Optional folder prefix in S3
 *  }
 * }
 * ```
 */
const S3FileWidget = (props) => {
  const { id, value, onChange, isDisabled, widgetOptions = {}, pathname } = props;
  const [fileType, setFileType] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const intl = useIntl();
  const dispatch = useDispatch();

  // Get upload state from Redux for this specific field (using subrequest)
  const uploadRequest = useSelector(
    (state) => state.s3Upload?.subrequests?.[id] || {}
  );
  const { loading, loaded, error, data } = uploadRequest;

  // Get current user from Redux
  const currentUser = useSelector((state) => state.users?.user);
  const username = currentUser?.id || currentUser?.username || `upload-${Date.now()}`;

  const s3folder = widgetOptions.s3folder || username;

  // Handle existing value for preview
  useEffect(() => {
    if (value && typeof value === 'string') {
      // Check if it's an image based on extension
      const ext = value.split('.').pop()?.toLowerCase();
      const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];
      if (imageExts.includes(ext)) {
        setFileType(true);
        // Construct S3 URL from filename (username/filename)
        // You'll need to configure the S3 base URL
        // For now, we'll just show the filename
        setFilePreview(null);
      } else {
        setFileType(false);
      }
    }
  }, [value]);

  // Handle successful upload
  useEffect(() => {
    if (loaded && data && data.success) {
      // Store only the filename in the field
      onChange(id, data.filename);
    }
  }, [loaded, data, id, onChange]);

  /**
   * Drop handler - uploads file to S3
   * @method onDrop
   * @param {array} files File objects
   * @returns {undefined}
   */
  const onDrop = (files) => {
    const file = files[0];
    if (!validateFileUploadSize(file, intl.formatMessage)) return;

    // Preview for images
    if (imageMimetypes.includes(file.type)) {
      setFileType(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }

    // Dispatch upload action
    const baseUrl = getBaseUrl(pathname) || '';
    const options = {};
    if (s3folder) {
      options.folder = s3folder;
    }

    dispatch(uploadS3File(baseUrl, file, options, id));
  };

  const handleDelete = () => {
    onChange(id, null);
    setFileType(false);
    setFilePreview(null);
  };

  return (
    <FormFieldWrapper {...props}>
      {error && (
        <Message negative>
          <Message.Header>{intl.formatMessage(messages.uploadError)}</Message.Header>
          <p>{error.message || error}</p>
        </Message>
      )}

      <Dropzone onDrop={onDrop} disabled={loading || isDisabled}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div className="file-widget-dropzone s3-file-widget" {...getRootProps()}>
            {(isDragActive || loading) && (
              <Dimmer active>
                {loading && (
                  <Loader indeterminate>
                    {intl.formatMessage(messages.uploading)}
                  </Loader>
                )}
              </Dimmer>
            )}

            {fileType && filePreview ? (
              <Image
                className="image-preview"
                id={`field-${id}-image`}
                size="small"
                src={filePreview}
              />
            ) : (
              <div className="dropzone-placeholder">
                <Icon name={uploadSVG} size="64px" color="#878f93" />
                {isDragActive ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.releaseDrag)}
                  </p>
                ) : value ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.editFile)}
                  </p>
                ) : (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.fileDrag)}
                  </p>
                )}
              </div>
            )}

            <label className="label-file-widget-input">
              {value
                ? intl.formatMessage(messages.replaceFile)
                : intl.formatMessage(messages.addNewFile)}
            </label>
            <input
              {...getInputProps({
                type: 'file',
                style: { display: 'none' },
              })}
              id={`field-${id}`}
              name={id}
              type="file"
              disabled={isDisabled || loading}
            />
          </div>
        )}
      </Dropzone>

      <div className="field-file-name">
        {value && (
          <>
            <div className="file-info">
              <strong>{value}</strong>
            </div>
            <Button
              type="button"
              icon
              basic
              className="delete-button"
              aria-label="delete file"
              disabled={isDisabled || loading}
              onClick={handleDelete}
            >
              <Icon name={deleteSVG} size="20px" />
            </Button>
          </>
        )}
      </div>
    </FormFieldWrapper>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
S3FileWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string, // S3 filename (e.g., "username/file.pdf")
  onChange: PropTypes.func.isRequired,
  wrapped: PropTypes.bool,
  isDisabled: PropTypes.bool,
  pathname: PropTypes.string,
  widgetOptions: PropTypes.shape({
    s3folder: PropTypes.string,
  }),
};

/**
 * Default properties.
 * @property {Object} defaultProps Default properties.
 * @static
 */
S3FileWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  isDisabled: false,
  widgetOptions: {},
};

export default S3FileWidget;
